import classNames from "classnames";
import React, { CSSProperties } from "react";
import { hasValue } from "Tools";
import { SimpleFieldProps, useSimpleFieldProps } from "./FormTools";
import { ErrorMessage } from "./Tools";

interface DropdownProps<T> {
  options: (readonly [T, React.ReactNode])[];
  style?: CSSProperties;
  isDisabled?: boolean;
  autoComplete?: string;
}

export function Dropdown<T extends string>(
  props: DropdownProps<T> & {
    field: SimpleFieldProps<T>;
    placeholder?: never;
    showPlaceHolderAfterItemSelected?: never;
  },
): JSX.Element;
export function Dropdown<T extends string>(
  props: DropdownProps<T> & {
    field: SimpleFieldProps<T | null>;
    placeholder?: string;
    showPlaceHolderAfterItemSelected?: true;
  },
): JSX.Element;
export function Dropdown<T extends string | null>(
  props: DropdownProps<T> & {
    field: SimpleFieldProps<T>;
    placeholder?: string;
    showPlaceHolderAfterItemSelected?: true;
  },
) {
  const field = useSimpleFieldProps(props.field);
  const getValueFromElement = (e: HTMLSelectElement) => {
    const result = e.value;
    return (result !== "" ? result : null) as T;
  };
  return (
    <div className="control" style={props.style}>
      <div
        className={classNames("select", {
          "has-value-selected":
            field.value !== "" && hasValue<string | null>(field.value),
        })}
        style={{ width: "100%" }}
      >
        <select
          id={field.name}
          name={field.name}
          className={classNames({
            "input-validation-error": field.error,
          })}
          value={field.value ?? ""}
          style={{ width: "100%" }}
          onFocus={async e => {
            await field.onFocus(getValueFromElement(e.target));
          }}
          onChange={async e => {
            await field.onChange(getValueFromElement(e.target));
          }}
          onBlur={async e => {
            await field.onBlur(getValueFromElement(e.target));
          }}
          disabled={props.isDisabled}
          autoComplete={props.autoComplete}
        >
          {props.placeholder !== undefined && (
            <option
              value=""
              hidden={props.showPlaceHolderAfterItemSelected !== true}
              disabled
            >
              {props.placeholder}
            </option>
          )}
          {props.options.map(
            ([key, value]) =>
              key !== null && (
                <option key={key} value={key}>
                  {value}
                </option>
              ),
          )}
        </select>
      </div>
      <ErrorMessage>{props.field.error}</ErrorMessage>
    </div>
  );
}

export function objToDropdownOptions<K extends string | number, V>(obj: {
  [k in K]: V;
}) {
  return Object.entries(obj) as [K, V][];
}

export function enumToDropdownOptions<K extends string, V>(obj: {
  [k in K]: V;
}) {
  return Object.values<V>(obj).map(v => [v, v] as const);
}
